import { useState, useEffect, useRef, useCallback } from 'react'
import { Search, X, User, Mail, Loader2 } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { userService, type UserSearchItem } from '@/lib/api/services'
import { getAvatarColor } from '@/lib/avatar-colors'

interface SelectedRecipient {
  id: string
  email: string
  fullName: string
  avatarUrl?: string
  isExternal: boolean
}

interface UserSearchComboboxProps {
  selectedRecipients: SelectedRecipient[]
  onRecipientsChange: (recipients: SelectedRecipient[]) => void
  placeholder?: string
  disabled?: boolean
}

export function UserSearchCombobox({
  selectedRecipients,
  onRecipientsChange,
  placeholder = 'Add people or enter email addresses...',
  disabled = false,
}: UserSearchComboboxProps) {
  const [query, setQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [results, setResults] = useState<UserSearchItem[]>([])
  const [isExternalEmail, setIsExternalEmail] = useState(false)
  const [externalEmail, setExternalEmail] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<NodeJS.Timeout | null>(null)

  const searchUsers = useCallback(async (searchQuery: string) => {
    if (!searchQuery.trim()) {
      setResults([])
      setIsExternalEmail(false)
      setExternalEmail(null)
      return
    }

    setLoading(true)
    try {
      const result = await userService.searchUsers(searchQuery, 10)
      // Filter out already selected users
      const filteredUsers = result.users.filter(
        (user) => !selectedRecipients.some((r) => r.id === user.id)
      )
      setResults(filteredUsers)
      setIsExternalEmail(result.isExternalEmail)
      setExternalEmail(result.externalEmail)
    } catch (error) {
      console.error('Failed to search users:', error)
      setResults([])
    } finally {
      setLoading(false)
    }
  }, [selectedRecipients])

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    if (query.trim()) {
      debounceRef.current = setTimeout(() => {
        searchUsers(query)
      }, 300)
    } else {
      setResults([])
      setIsExternalEmail(false)
      setExternalEmail(null)
    }

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [query, searchUsers])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelectUser = (user: UserSearchItem) => {
    const newRecipient: SelectedRecipient = {
      id: user.id,
      email: user.email,
      fullName: user.fullName,
      avatarUrl: user.avatarUrl,
      isExternal: false,
    }
    onRecipientsChange([...selectedRecipients, newRecipient])
    setQuery('')
    setIsOpen(false)
    inputRef.current?.focus()
  }

  const handleSelectExternalEmail = () => {
    if (!externalEmail) return

    // Check if this email is already selected
    if (selectedRecipients.some((r) => r.email.toLowerCase() === externalEmail.toLowerCase())) {
      return
    }

    const newRecipient: SelectedRecipient = {
      id: `external-${externalEmail}`,
      email: externalEmail,
      fullName: externalEmail,
      isExternal: true,
    }
    onRecipientsChange([...selectedRecipients, newRecipient])
    setQuery('')
    setIsOpen(false)
    inputRef.current?.focus()
  }

  const handleRemoveRecipient = (recipientId: string) => {
    onRecipientsChange(selectedRecipients.filter((r) => r.id !== recipientId))
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && isExternalEmail && externalEmail) {
      e.preventDefault()
      handleSelectExternalEmail()
    } else if (e.key === 'Backspace' && !query && selectedRecipients.length > 0) {
      const lastRecipient = selectedRecipients[selectedRecipients.length - 1]
      if (lastRecipient) {
        handleRemoveRecipient(lastRecipient.id)
      }
    }
  }

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  const showDropdown = isOpen && (results.length > 0 || isExternalEmail || loading)

  return (
    <div ref={containerRef} className="relative">
      <div
        className={cn(
          'flex flex-wrap items-center gap-1.5 p-2 border rounded-lg bg-white dark:bg-gray-900',
          'focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
        onClick={() => !disabled && inputRef.current?.focus()}
      >
        {selectedRecipients.map((recipient) => (
          <Badge
            key={recipient.id}
            variant="secondary"
            className="flex items-center gap-1.5 px-2 py-1 text-sm"
          >
            {recipient.isExternal ? (
              <Mail className="h-3.5 w-3.5 text-muted-foreground" />
            ) : (
              <Avatar className="h-4 w-4">
                <AvatarImage src={recipient.avatarUrl} />
                <AvatarFallback className={`text-[8px] ${getAvatarColor(recipient.id || recipient.email).bg} ${getAvatarColor(recipient.id || recipient.email).text}`}>
                  {getInitials(recipient.fullName)}
                </AvatarFallback>
              </Avatar>
            )}
            <span className="max-w-[150px] truncate">{recipient.fullName}</span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                handleRemoveRecipient(recipient.id)
              }}
              className="ml-1 hover:text-destructive"
              disabled={disabled}
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
        <div className="flex-1 flex items-center gap-2 min-w-[200px]">
          <Search className="h-4 w-4 text-muted-foreground shrink-0" />
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              setIsOpen(true)
            }}
            onFocus={() => setIsOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder={selectedRecipients.length === 0 ? placeholder : ''}
            className="border-0 p-0 h-auto focus-visible:ring-0 focus-visible:ring-offset-0"
            disabled={disabled}
          />
          {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        </div>
      </div>

      {showDropdown && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-900 border rounded-lg shadow-lg max-h-64 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              {results.map((user) => (
                <button
                  key={user.id}
                  type="button"
                  className="flex items-center gap-3 w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-800"
                  onClick={() => handleSelectUser(user)}
                >
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user.avatarUrl} />
                    <AvatarFallback className={`${getAvatarColor(user.id || user.email).bg} ${getAvatarColor(user.id || user.email).text}`}>{getInitials(user.fullName)}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{user.fullName}</div>
                    <div className="text-sm text-muted-foreground truncate">{user.email}</div>
                  </div>
                  <User className="h-4 w-4 text-muted-foreground shrink-0" />
                </button>
              ))}
              {isExternalEmail && externalEmail && (
                <button
                  type="button"
                  className="flex items-center gap-3 w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-800 border-t"
                  onClick={handleSelectExternalEmail}
                >
                  <div className="h-8 w-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                    <Mail className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{externalEmail}</div>
                    <div className="text-sm text-muted-foreground">
                      Will receive an invitation
                    </div>
                  </div>
                  <Badge variant="outline" className="shrink-0">
                    External
                  </Badge>
                </button>
              )}
              {!loading && results.length === 0 && !isExternalEmail && query.trim() && (
                <div className="px-3 py-4 text-center text-muted-foreground">
                  No users found
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
